import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from "../../services/api";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "./CashierDashboardStatistics.css";

/* ================================
   Icons
================================ */
const ReceiptIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
        <path d="M14 2v6h6" />
        <path d="M9 14h6" />
        <path d="M9 17h6" />
    </svg>
);

const ZapIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const RefundIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <path d="M12 2v8l-4-4" />
        <path d="M8 6L4 10" />
        <path d="M21.17 13.17a2.5 2.5 0 0 0 0-3.54l-2.63-2.63a2.5 2.5 0 0 0-3.54 0L3 21.25V21c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v0a2.5 2.5 0 0 0 0-3.54Z" />
    </svg>
);

const PaymentIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
);
const CalendarIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
/* ================================
   Helpers
================================ */
const formatCurrency = (amount) => {
    if (typeof amount !== 'number') amount = Number(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatCurrencyPdf = (amount) => {
    if (typeof amount !== 'number') amount = Number(amount) || 0;
    return "Rs. " + new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    let date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [y, m, d] = dateString.split('-');
        date = new Date(`${y}-${m}-${d}T00:00:00`);
    } else {
        date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return 'N/A';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = date.getFullYear();
    return `${dd}-${mm}-${yy}`;
};
function usePagination(data, pageSize = 10) {
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil((data?.length || 0) / pageSize));

    useEffect(() => {
        setPage(1);
    }, [data]);

    const paginated = (data || []).slice((page - 1) * pageSize, page * pageSize);

    return { page, setPage, totalPages, paginated };
}

function PaginationControls({ page, totalPages, onChange }) {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);

    return (
        <nav className="d-flex justify-content-between align-items-center mt-3">
            <span className="small text-muted">Page {page} of {totalPages}</span>
            <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => onChange(page - 1)}>Prev</button>
                </li>
                {pages.map((p) => (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => onChange(p)}>{p}</button>
                    </li>
                ))}
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => onChange(page + 1)}>Next</button>
                </li>
            </ul>
        </nav>
    );
}
/* ================================
   Reusable UI
================================ */
function MainStatCard({ title, description, Icon, iconBg, iconColor, isSelected, onClick }) {
    const cardClasses = `card shadow-sm rounded-3 p-4 h-100 cursor-pointer ${isSelected ? 'border border-2 border-primary shadow' : 'border-0'}`;
    return (
        <div onClick={onClick} className={cardClasses} style={{ transition: 'box-shadow 0.3s ease, border 0.3s ease' }}>
            <div className="d-flex justify-content-between align-items-start">
                <div>
                    <h3 className="h5 fw-bold text-dark mb-1">{title}</h3>
                    <p className="small text-muted mt-1 mb-0">{description}</p>
                </div>
                <div className={`p-3 rounded-circle ${iconBg}`}><Icon className={`h-6 w-6 ${iconColor}`} /></div>
            </div>
        </div>
    );
}

function StatCard({ title, amount, count, Icon, iconBg, iconColor, isSelected, onClick }) {
    const cardClasses = `card shadow-sm rounded-3 p-4 h-100 cursor-pointer ${isSelected ? 'border border-2 border-primary shadow' : 'border-0'}`;
    return (
        <div onClick={onClick} className={cardClasses} style={{ transition: 'box-shadow 0.3s ease, border 0.3s ease' }}>
            <div className="d-flex justify-content-between align-items-start">
                <div>
                    <h3 className="text-muted text-uppercase small fw-medium">{title}</h3>
                    <p className="h3 fw-bold text-dark mt-2 mb-1">{formatCurrency(amount)}</p>
                    <p className="small text-muted mt-1 mb-0">{count} {count === 1 ? 'record' : 'records'}</p>
                </div>
                <div className={`p-3 rounded-circle ${iconBg}`}>
                    <Icon className={`h-6 w-6 ${iconColor}`} />
                </div>
            </div>
        </div>
    );
}

function DetailBox({ title, children }) {
    return (
        <div className="card shadow-sm rounded-3 border-0">
            <div className="card-body">
                <h2 className="h4 fw-bold text-dark mb-4">{title}</h2>
                {children}
            </div>
        </div>
    );
}

/* ================================
   Tables
================================ */
// function RefundsTable({ refundData }) {
//     if (!refundData || refundData.length === 0) return <p className="text-muted">No refund data available.</p>;
//     return (
//         <div className="table-responsive">
//             <table className="table table-striped table-hover align-middle">
//                 <thead className="table-light">
//                     <tr>
//                         <th>S.no</th>
//                         <th>Roll No.</th>
//                         <th>Name</th>
//                         <th>Course</th>
//                         <th>Technology</th>
//                         <th>Amount</th>
//                         <th>Reason</th>
//                         <th>Term</th>
//                         <th>Payment ID</th>
//                         <th>Refund Date</th>
//                         <th>College</th>
//                         <th>Branch</th>

//                     </tr>
//                 </thead>
//                 <tbody>
//                     {refundData.map((item, index) => (
//                         <tr key={item._id || item.payment_id}>
//                             <td className="fw-medium">{index + 1}</td>
//                             <td className="fw-medium">{item.roll_no || 'N/A'}</td>
//                             <td>{item.name || item.first_name || 'N/A'}</td>
//                             <td>{item.course_name || 'N/A'}</td>
//                             <td>{item.technology_name || 'N/A'}</td>
//                             <td className="fw-medium text-danger">{formatCurrency(item.refund_amount || item.amount || 0)}</td>
//                             <td>{item.refund_reason || 'N/A'}</td>
//                             <td>{item.term || item.term_name || 'N/A'}</td>
//                             <td className="small text-muted">{item.payment_id || 'N/A'}</td>
//                             <td className="small">{formatDate(item.refund_given_date || item.refund_date)}</td>
//                             <td>{item.college || 'N/A'}</td>
//                             <td>{Array.isArray(item.branch) ? item.branch.join(', ') : item.branch || 'N/A'}</td>
//                         </tr>
//                     ))}
//                 </tbody>
//             </table>
//         </div>
//     );
// }

function RefundsTable({ refundData }) {
    const { page, setPage, totalPages, paginated } = usePagination(refundData, 10);

    if (!refundData || refundData.length === 0) return <p className="text-muted">No refund data available.</p>;
    return (
        <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
                <thead className="table-light">
                    <tr>
                        <th>S.no</th>
                        <th>Roll No.</th>
                        <th>Name</th>
                        <th>Course</th>
                        <th>Technology</th>
                        <th>Amount</th>
                        <th>Reason</th>
                        <th>Term</th>
                        <th>Payment ID</th>
                        <th>Refund Date</th>
                        <th>College</th>
                        <th>Branch</th>
                    </tr>
                </thead>
                <tbody>
                    {paginated.map((item, index) => (
                        <tr key={item._id || item.payment_id}>
                            <td className="fw-medium">{(page - 1) * 10 + index + 1}</td>
                            <td className="fw-medium">{item.roll_no || 'N/A'}</td>
                            <td>{item.name || item.first_name || 'N/A'}</td>
                            <td>{item.course_name || 'N/A'}</td>
                            <td>{item.technology_name || 'N/A'}</td>
                            <td className="fw-medium text-danger">{formatCurrency(item.refund_amount || item.amount || 0)}</td>
                            <td>{item.refund_reason || 'N/A'}</td>
                            <td>{item.term || item.term_name || 'N/A'}</td>
                            <td className="small text-muted">{item.payment_id || 'N/A'}</td>
                            <td className="small">{formatDate(item.refund_given_date || item.refund_date)}</td>
                            <td>{item.college || 'N/A'}</td>
                            <td>{Array.isArray(item.branch) ? item.branch.join(', ') : item.branch || 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <PaginationControls page={page} totalPages={totalPages} onChange={setPage} />
        </div>
    );
}

// function PaymentsTable({ paymentsData }) {
//     if (!paymentsData || paymentsData.length === 0) return <p className="text-muted">No payment data available.</p>;
//     return (
//         <div className="table-responsive">
//             <table className="table table-striped table-hover align-middle">
//                 <thead className="table-light">
//                     <tr>
//                         <th>S.no</th>
//                         <th>Roll No.</th>
//                         <th>Name</th>
//                         <th>Course</th>
//                         <th>Technology</th>
//                         <th>Amount</th>
//                         <th>Discount</th>
//                         <th>Term</th>
//                         <th>Payment ID</th>
//                         <th>Payment Date</th>
//                         <th>College</th>
//                         <th>Branch</th>
//                         <th>Paid Date</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {paymentsData.map((item, index) => (
//                         <tr key={item._id || item.payment_id}>
//                             <td className="fw-medium">{index + 1}</td>
//                             <td className="fw-medium">{item.roll_no || 'N/A'}</td>
//                             <td>{item.name || item.first_name || 'N/A'}</td>
//                             <td>{item.course_name || 'N/A'}</td>
//                             <td>{item.technology_name || 'N/A'}</td>
//                             <td className="fw-medium text-success">{formatCurrency(item.amount)}</td>
//                             <td>{item.discount || 0}</td>
//                             <td>{item.term_name || item.term || 'N/A'}</td>
//                             <td className="small text-muted">{item.payment_id}</td>
//                             <td className="small">{formatDate(item.payment_date)}</td>
//                             <td>{item.college || 'N/A'}</td>
//                             <td>{Array.isArray(item.branch) ? item.branch.join(', ') : item.branch || 'N/A'}</td>
//                             <td>
//                                 {item.payment_date ? (() => {
//                                     const d = new Date(item.payment_date);
//                                     const day = String(d.getDate()).padStart(2, "0");
//                                     const month = String(d.getMonth() + 1).padStart(2, "0");
//                                     const year = d.getFullYear();

//                                     let hours = d.getHours();
//                                     const minutes = String(d.getMinutes()).padStart(2, "0");
//                                     const ampm = hours >= 12 ? "PM" : "AM";
//                                     hours = hours % 12 || 12; // convert 24h → 12h

//                                     return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
//                                 })() : "N/A"}
//                             </td>

//                         </tr>
//                     ))}
//                 </tbody>
//             </table>
//         </div>
//     );
// }

function PaymentsTable({ paymentsData }) {
    const { page, setPage, totalPages, paginated } = usePagination(paymentsData, 10);

    if (!paymentsData || paymentsData.length === 0) return <p className="text-muted">No payment data available.</p>;
    return (
        <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
                <thead className="table-light">
                    <tr>
                        <th>S.no</th>
                        <th>Roll No.</th>
                        <th>Name</th>
                        <th>Course</th>
                        <th>Technology</th>
                        <th>Amount</th>
                        <th>Discount</th>
                        <th>Term</th>
                        <th>Payment ID</th>
                        <th>Payment Date</th>
                        <th>College</th>
                        <th>Branch</th>
                        <th>Paid Date</th>
                    </tr>
                </thead>
                <tbody>
                    {paginated.map((item, index) => (
                        <tr key={item._id || item.payment_id}>
                            <td className="fw-medium">{(page - 1) * 10 + index + 1}</td>
                            <td className="fw-medium">{item.roll_no || 'N/A'}</td>
                            <td>{item.name || item.first_name || 'N/A'}</td>
                            <td>{item.course_name || 'N/A'}</td>
                            <td>{item.technology_name || 'N/A'}</td>
                            <td className="fw-medium text-success">{formatCurrency(item.amount)}</td>
                            <td>{item.discount || 0}</td>
                            <td>{item.term_name || item.term || 'N/A'}</td>
                            <td className="small text-muted">{item.payment_id}</td>
                            <td className="small">{formatDate(item.payment_date)}</td>
                            <td>{item.college || 'N/A'}</td>
                            <td>{Array.isArray(item.branch) ? item.branch.join(', ') : item.branch || 'N/A'}</td>
                            <td>
                                {item.payment_date ? (() => {
                                    const d = new Date(item.payment_date);
                                    const day = String(d.getDate()).padStart(2, "0");
                                    const month = String(d.getMonth() + 1).padStart(2, "0");
                                    const year = d.getFullYear();

                                    let hours = d.getHours();
                                    const minutes = String(d.getMinutes()).padStart(2, "0");
                                    const ampm = hours >= 12 ? "PM" : "AM";
                                    hours = hours % 12 || 12;

                                    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
                                })() : "N/A"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <PaginationControls page={page} totalPages={totalPages} onChange={setPage} />
        </div>
    );
}

/* ================================
   XLSX loader once
================================ */
function useEnsureXlsx() {
    useEffect(() => {
        const id = 'xlsx-script';
        if (!document.getElementById(id)) {
            const s = document.createElement('script');
            s.id = id;
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            s.async = true;
            document.head.appendChild(s);
        }
    }, []);
}

/* ================================
   Dashboard Wrapper
================================ */
export default function CashierDashboard() {
    const [activeSection, setActiveSection] = useState('payments');
    useEnsureXlsx();

    return (
        <div className="px-4 px-md-5 bg-light min-vh-100 font-sans-serif">
            <div className="row g-4">
                <div className="col-md-3">
                    <MainStatCard
                        title="Payments"
                        description="View your payment history"
                        Icon={PaymentIcon}
                        iconBg="bg-success-subtle"
                        iconColor="text-success"
                        isSelected={activeSection === 'payments'}
                        onClick={() => setActiveSection('payments')}
                    />
                </div>
                <div className="col-md-3">
                    <MainStatCard
                        title="Refunds"
                        description="View your refund history"
                        Icon={RefundIcon}
                        iconBg="bg-danger-subtle"
                        iconColor="text-danger"
                        isSelected={activeSection === 'refunds'}
                        onClick={() => setActiveSection('refunds')}
                    />
                </div>
            </div>

            <div className="mt-4">
                {activeSection === 'payments' ? <CashierPaymentsStatistics /> : <CashierRefundsStatistics />}
            </div>
        </div>
    );
}

/* ================================
   Refunds (cashcounter-only, separate APIs)
================================ */
const CashierRefundsStatistics = () => {
    const [selectedStat, setSelectedStat] = useState(null);
    const [todayRefundData, setTodayRefundData] = useState(null); // { total_refund_amount, count, data: [] }
    const [totalRefundData, setTotalRefundData] = useState(null); // { total_refund_amount, count, data: [] }
    const [pageIsLoading, setPageIsLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [user, setUser] = useState(null);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api/';

    useEffect(() => {
        const auth = JSON.parse(localStorage.getItem('authUser'));
        setUser(auth || null);
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            if (!user || !user.college) {
                if (user === null) {
                    setPageError('User not logged in.');
                    setPageIsLoading(false);
                }
                return;
            }

            setPageIsLoading(true);
            setPageError(null);

            const reqToday = axios.post(`${backendUrl}get-overall-refund-data-by-cashcounter-name-today`, {
                refund_cashcounter: user.college,
            });
            const reqTotal = axios.post(`${backendUrl}get-overall-refund-data-by-cashcounter-name-total`, {
                refund_cashcounter: user.college,
            });

            try {
                const [rToday, rTotal] = await Promise.allSettled([reqToday, reqTotal]);
                let err = null;

                if (rToday.status === 'fulfilled' && rToday.value.status === 200 && rToday.value.data?.success) {
                    setTodayRefundData(rToday.value.data);
                } else {
                    err = 'Could not fetch Today refunds.';
                }

                if (rTotal.status === 'fulfilled' && rTotal.value.status === 200 && rTotal.value.data?.success) {
                    setTotalRefundData(rTotal.value.data);
                    console.log(rTotal.value.data)
                } else {
                    err = (err || '') + ' Could not fetch Total refunds.';
                }

                if (err) setPageError(err);
            } catch {
                setPageError('Unexpected error while fetching refunds.');
            } finally {
                setPageIsLoading(false);
            }
        };

        fetchAllData();
    }, [backendUrl, user]);

    const stats = [
        {
            id: 'today',
            title: "Today's Refunds",
            amount: todayRefundData?.total_refund_amount || 0,
            count: todayRefundData?.count || 0,
            icon: ZapIcon,
            iconBg: 'bg-success-subtle',
            iconColor: 'text-success',
        },
        {
            id: 'total',
            title: 'Total Refunds',
            amount: totalRefundData?.total_refund_amount || 0,
            count: totalRefundData?.count || 0,
            icon: ReceiptIcon,
            iconBg: 'bg-primary-subtle',
            iconColor: 'text-primary',
        },
    ];

    if (pageIsLoading) {
        return (
            <div className="p-4 p-md-5 d-flex justify-content-center align-items-center">
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="p-4 p-md-5">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error!</h4>
                    <p>Failed to load refund statistics.</p>
                    <hr />
                    <p className="mb-0 small">{pageError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-0">
            <div className="row g-4">
                {stats.map((s) => (
                    <div className="col-md-4" key={s.id}>
                        <StatCard
                            title={s.title}
                            amount={s.amount}
                            count={s.count}
                            Icon={s.icon}
                            iconBg={s.iconBg}
                            iconColor={s.iconColor}
                            isSelected={selectedStat === s.id}
                            onClick={() => setSelectedStat(selectedStat === s.id ? null : s.id)}
                        />
                    </div>
                ))}
            </div>

            <div className="mt-2">
                <DetailBox
                    title={
                        selectedStat === 'total'
                            ? 'All Your Refunds'
                            : selectedStat === 'today'
                                ? "Today’s Refunds"
                                : 'Details'
                    }
                >
                    {selectedStat === 'today' && <RefundsTable refundData={todayRefundData?.data || []} />}
                    {selectedStat === 'total' && <RefundsTable refundData={totalRefundData?.data || []} />}
                    {!selectedStat && (
                        <div className="text-center border-dashed p-5 rounded-3">
                            <p className="text-muted fw-medium mb-0">Click on a card to see more details.</p>
                        </div>
                    )}
                </DetailBox>
            </div>
        </div>
    );
};

/* ================================
   Payments (cashier-wise, includes refunds in API; NET on cards)
================================ */
const CashierPaymentsStatistics = () => {
    const [selectedStat, setSelectedStat] = useState(null);

    const [todayData, setTodayData] = useState(null);  // { today_payments, today_refunds, summary }
    const [totalData, setTotalData] = useState(null);  // { all_payments, refunds_given, summary }

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showTodayModal, setShowTodayModal] = useState(false);
    const [showTotalModal, setShowTotalModal] = useState(false);

    const [selectedDate, setSelectedDate] = useState('');
    const [dateData, setDateData] = useState(null); // { today_payments, today_refunds, summary }
    const [isDateLoading, setIsDateLoading] = useState(false);
    const [dateError, setDateError] = useState(null);
    const [showDateModal, setShowDateModal] = useState(false);



    const [user, setUser] = useState(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const auth = JSON.parse(localStorage.getItem('authUser'));
        setUser(auth || null);
    }, []);

    useEffect(() => {
        if (!user || !user.first_name) return;

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const reqToday = api.post(`${backendUrl}course-payments-overall-today-by-cashier-name`, {
                    cashier_name: user.first_name,
                });
                const reqTotal = api.post(`${backendUrl}course-payments-overall-by-cashier-total`, {
                    cashier_name: user.first_name,
                });

                const [rToday, rTotal] = await Promise.allSettled([reqToday, reqTotal]);

                if (rToday.status === 'fulfilled' && rToday.value.data?.success) {
                    setTodayData(rToday.value.data.data);
                } else {
                    setError("Failed to fetch today's payments.");
                }

                if (rTotal.status === 'fulfilled' && rTotal.value.data?.success) {
                    setTotalData(rTotal.value.data.data);
                } else {
                    setError('Failed to fetch total payments.');
                }
            } catch {
                setError('Unexpected error occurred.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [user, backendUrl]);


    const handleDateChange = async (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);

        if (!newDate) {
            setDateData(null);
            setDateError(null);
            return;
        }

        setIsDateLoading(true);
        setDateError(null);
        setDateData(null);

        try {
            const response = await api.post(`${backendUrl}course-payments-overall-by-cashier-by-date`, {
                date: newDate,
                cashier_name: user.first_name,
            });

            if (response.data?.success) {
                const api = response.data.data; // { all_payments, refunds_given, summary }
                setDateData({
                    today_payments: api.all_payments || [],
                    today_refunds: api.refunds_given || [],
                    summary: api.summary || {},
                });
                setSelectedStat('byDate');
            } else {
                setDateError('No data found for this date.');
            }
        } catch {
            setDateError('Failed to fetch data.');
        } finally {
            setIsDateLoading(false);
        }
    };

    // Compute NET safely for cards
    const todaySummary = todayData?.summary || {};
    const totalSummary = totalData?.summary || {};

    // const todayNet =
    //     (todaySummary.net_amount_in_cashcounter ??
    //         todaySummary.net_amount ??
    //         (todaySummary.total_payments_today ?? todaySummary.total_payments_amount ?? 0) -
    //         (todaySummary.total_refunds_today ?? todaySummary.total_refunds_amount ?? 0)) || 0;
    const todayNet =
        todaySummary.total_payments_today ??
        todaySummary.total_payments_amount ??
        0;

    const totalNet =
        (totalSummary.net_amount_in_cashcounter ??
            totalSummary.net_amount ??
            (totalSummary.total_payments_amount ?? 0) - (totalSummary.total_refunds_amount ?? 0)) || 0;

    const dateSummary = dateData?.summary || {};

    const dateNet =
        dateSummary.total_payments_amount ?? 0;

    const cards = [
        {
            id: 'today',
            title: "Today's Summary",
            net: todayNet,
            paymentsAmount: todaySummary.total_payments_today ?? todaySummary.total_payments_amount ?? 0,
            paymentsCount: todaySummary.total_payments_count ?? 0,
            refundsAmount: todaySummary.total_refunds_today ?? todaySummary.total_refunds_amount ?? 0,
            refundsCount: todaySummary.total_refunds_count ?? 0,
            iconBg: 'bg-success-subtle',
            iconColor: 'text-success',
            Icon: ZapIcon,
        },
        {
            id: 'total',
            title: 'Total Summary',
            net: totalNet,
            paymentsAmount: totalSummary.total_payments_amount ?? 0,
            paymentsCount: totalSummary.total_payments_count ?? 0,
            refundsAmount: totalSummary.total_refunds_amount ?? 0,
            refundsCount: totalSummary.total_refunds_count ?? 0,
            iconBg: 'bg-primary-subtle',
            iconColor: 'text-primary',
            Icon: ReceiptIcon,
        },
    ];

    if (isLoading) {
        return (
            <div className="p-5 text-center">
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    if (error) return <div className="alert alert-danger">{error}</div>;

    return (
        <div className="p-0">
            {/* Cards with NET + breakdown */}
            <div className="row g-4">
                {cards.map((c) => (
                    <div className="col-md-4" key={c.id}>
                        <div
                            className={`card shadow-sm rounded-3 p-4 h-100 cursor-pointer ${selectedStat === c.id ? 'border border-2 border-primary shadow' : 'border-0'}`}
                            onClick={() => setSelectedStat(selectedStat === c.id ? null : c.id)}
                            style={{ transition: 'box-shadow 0.3s ease, border 0.3s ease' }}
                        >
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h3 className="text-muted text-uppercase small fw-medium">{c.title}</h3>
                                    <p className="h3 fw-bold text-dark mt-2 mb-1">
{formatCurrency(c.net)}{" "}                                        <span className="h6 text-muted fw-normal ms-2">
                                            (Total Payments)
                                        </span>
                                    </p>
                                    <div className="d-flex justify-content-between small mt-2">
                                        <span className="text-success">
                                            Payments: {formatCurrency(c.paymentsAmount)} ({c.paymentsCount})
                                        </span>
                                        <span className="text-danger ms-2">
                                            Refunds: {formatCurrency(c.refundsAmount)} ({c.refundsCount})
                                        </span>
                                    </div>
                                </div>
                                <div className={`p-3 rounded-circle ${c.iconBg}`}>
                                    <c.Icon className={`h-6 w-6 ${c.iconColor}`} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="col-md-4">
                    <div
                        className={`card shadow-sm rounded-3 p-4 h-100 ${selectedStat === 'byDate' ? 'border border-2 border-primary shadow' : ''} ${dateData ? 'cursor-pointer' : ''} ${selectedStat !== 'byDate' ? 'border-0' : ''}`}
                        onClick={() => {
                            if (dateData) setSelectedStat(selectedStat === 'byDate' ? null : 'byDate');
                        }}
                        style={{ transition: 'box-shadow 0.3s ease, border 0.3s ease' }}
                    >
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                                <h3 className="text-muted text-uppercase small fw-medium mb-2">Payments by Date</h3>
                                <input
                                    type="date"
                                    className="form-control form-control-sm mb-2"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isDateLoading}
                                />

                                {isDateLoading ? (
                                    <div className="d-flex align-items-center mt-2">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="ms-2 text-muted small">Loading...</span>
                                    </div>
                                ) : dateError ? (
                                    <p className="small text-danger mb-0 mt-2 fw-medium">{dateError}</p>
                                ) : dateData ? (
                                    <>
                                        <p className="h3 fw-bold text-dark mt-2 mb-1">
                                            {formatCurrency(dateNet)} <span className="h6 text-muted fw-normal ms-2">(Net)</span>
                                        </p>
                                        <div className="d-flex justify-content-between small mt-2">
                                            <span className="text-success">
                                                Payments: {formatCurrency(dateSummary.total_payments_amount || 0)} ({dateSummary.total_payments_count || 0})
                                            </span>
                                            <span className="text-danger ms-2">
                                                Refunds: {formatCurrency(dateSummary.total_refunds_amount || 0)} ({dateSummary.total_refunds_count || 0})
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <p className="small text-muted mb-0 mt-2">Select a date to see stats.</p>
                                )}
                            </div>
                            <div className="p-3 rounded-circle bg-info-subtle">
                                <CalendarIcon className="h-6 w-6 text-info" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>




            {/* Detail section */}
            <div className="mt-1">
                <DetailBox title={selectedStat === 'today' ? "Today's Summary" : selectedStat === 'total' ? 'Total Summary' : 'Details'}>
                    {selectedStat === 'today' && (
                        <>
                            <h5 className="fw-bold">Payments</h5>
                            <PaymentsTable paymentsData={todayData?.today_payments || []} />

                            <h5 className="fw-bold mt-4">Refunds</h5>
                            <RefundsTable refundData={todayData?.today_refunds || []} />

                            <div className="text-end mt-3">
                                <button className="btn btn-primary" onClick={() => setShowTodayModal(true)}>View Full Report</button>
                            </div>
                        </>
                    )}

                    {selectedStat === 'total' && (
                        <>
                            <h5 className="fw-bold">Payments</h5>
                            <PaymentsTable paymentsData={totalData?.all_payments || []} />

                            <h5 className="fw-bold mt-4">Refunds</h5>
                            <RefundsTable refundData={totalData?.refunds_given || []} />

                            <div className="text-end mt-3">
                                <button className="btn btn-primary" onClick={() => setShowTotalModal(true)}>View Full Report</button>
                            </div>
                        </>
                    )}

                    {selectedStat === 'byDate' && (
                        <>
                            <h5 className="fw-bold">Payments</h5>
                            <PaymentsTable paymentsData={dateData?.today_payments || []} />

                            <h5 className="fw-bold mt-4">Refunds</h5>
                            <RefundsTable refundData={dateData?.today_refunds || []} />

                            <div className="text-end mt-3">
                                <button className="btn btn-primary" onClick={() => setShowDateModal(true)}>View Full Report</button>
                            </div>
                        </>
                    )}

                    {!selectedStat && (
                        <div className="text-center p-5 border-dashed rounded-3">
                            <p className="text-muted">Click a card to view details.</p>
                        </div>
                    )}
                </DetailBox>
            </div>

            {/* Modals */}
            <PaymentsTodaySummaryModal
                show={showTodayModal}
                onClose={() => setShowTodayModal(false)}
                summaryData={todayData}
                cashier={user?.first_name}
            />
            <PaymentsTotalSummaryModal
                show={showTotalModal}
                onClose={() => setShowTotalModal(false)}
                summaryData={totalData}
                cashier={user?.first_name}
            />
            <PaymentsDateSummaryModal
                show={showDateModal}
                onClose={() => setShowDateModal(false)}
                summaryData={dateData}
                cashier={user?.first_name}
                date={selectedDate}
            />
        </div>
    );
};

/* ================================
   Payments Modals (Excel export)
================================ */
function PaymentsTodaySummaryModal({ show, onClose, summaryData, cashier }) {
    // const handleExcelDownload = () => {
    //     if (!window.XLSX) return alert("XLSX not loaded!");

    //     try {
    //         const summary = summaryData?.summary || {};
    //         const title = cashier === "All" ? "All Cashiers" : cashier;

    //         const ws_data = [
    //             [`Full Today Summary Report — ${title}`],
    //             [],
    //             ["Payments"],
    //             [
    //                 "S.no",
    //                 "Roll No.",
    //                 "Name",
    //                 "Term",
    //                 "Payment ID",
    //                 "Payment Date",
    //                 "College",
    //                 "Branch",
    //                 "Course",
    //                 "Technology",
    //                 "Amount",
    //             ]
    //         ];

    //         (summaryData?.today_payments || []).forEach((p, index) =>
    //             ws_data.push([
    //                 index + 1,
    //                 p.roll_no || "N/A",
    //                 p.name || p.first_name || "N/A",
    //                 p.term_name || "N/A",
    //                 p.payment_id || "N/A",
    //                 formatDate(p.payment_date),
    //                 p.college || "N/A",
    //                 Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
    //                 p.course_name || "N/A",
    //                 p.technology_name || "N/A",
    //                 p.amount || 0,
    //             ])
    //         );
    //         ws_data.push([]);
    //         ws_data.push(["Refunds"]);
    //         ws_data.push([
    //             "S.no",
    //             "Roll No.",
    //             "Name",
    //             "Reason",
    //             "Term",
    //             "Payment ID",
    //             "Refund Date",
    //             "College",
    //             "Refund Amount",
    //         ]);

    //         (summaryData?.today_refunds || []).forEach((r, index) =>
    //             ws_data.push([
    //                 index + 1,
    //                 r.roll_no || "N/A",
    //                 r.name || r.first_name || "N/A",
    //                 r.refund_reason || "N/A",
    //                 r.term_name || r.term || "N/A",
    //                 r.payment_id || "N/A",
    //                 formatDate(r.refund_date || r.refund_given_date),
    //                 r.college || "N/A",
    //                 r.refund_amount || r.amount || 0,
    //             ])
    //         );

    //         ws_data.push([]);
    //         ws_data.push(["Summary"]);
    //         ws_data.push(["", "", "", "", "", "", "", "", "", "Total Payments", summary.total_payments_today || summary.total_payments_amount || 0]);
    //         ws_data.push(["", "", "", "", "", "", "", "", "", "Total Refunds", summary.total_refunds_today || summary.total_refunds_amount || 0]);

    //         const netAmount =
    //             summary.net_amount_in_cashcounter ??
    //             summary.net_amount ??
    //             (summary.total_payments_today || summary.total_payments_amount || 0) -
    //             (summary.total_refunds_today || summary.total_refunds_amount || 0);

    //         ws_data.push(["", "", "", "", "", "", "", "", "", "Net Amount", netAmount]);

    //         const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
    //         const wb = window.XLSX.utils.book_new();
    //         window.XLSX.utils.book_append_sheet(wb, ws, "Today Summary");

    //         window.XLSX.writeFile(wb, `Today_Summary_${title}.xlsx`);
    //     } catch (err) {
    //         console.error(err);
    //     }
    // };

    const handleExcelDownload = () => {
        if (!window.XLSX) return alert("XLSX not loaded!");

        try {
            const summary = summaryData?.summary || {};
            const title = cashier === "All" ? "All Cashiers" : cashier;
            const today = formatDate(new Date().toISOString().split("T")[0]);

            const ws_data = [];
            const merges = [];

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Full Today Summary Report — ${title}`]);
            ws_data.push([]);

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Total Thub Payments from ${today} to ${today}`]);
            ws_data.push([
                "S.no",
                "Roll No.",
                "Name",
                "Term",
                "Payment ID",
                "Payment Date",
                "College",
                "Branch",
                "Course",
                "Technology",
                "Amount",
            ]);

            (summaryData?.today_payments || []).forEach((p, index) =>
                ws_data.push([
                    index + 1,
                    p.roll_no || "N/A",
                    p.name || p.first_name || "N/A",
                    p.term_name || "N/A",
                    p.payment_id || "N/A",
                    formatDate(p.payment_date),
                    p.college || "N/A",
                    Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                    p.course_name || "N/A",
                    p.technology_name || "N/A",
                    p.amount || 0,
                ])
            );
            ws_data.push([]);

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Total Thub Refunds from ${today} to ${today}`]);
            ws_data.push([
                "S.no",
                "Roll No.",
                "Name",
                "Reason",
                "Term",
                "Payment ID",
                "Refund Date",
                "College",
                "Refund Amount",
            ]);

            (summaryData?.today_refunds || []).forEach((r, index) =>
                ws_data.push([
                    index + 1,
                    r.roll_no || "N/A",
                    r.name || r.first_name || "N/A",
                    r.refund_reason || "N/A",
                    r.term_name || r.term || "N/A",
                    r.payment_id || "N/A",
                    formatDate(r.refund_date || r.refund_given_date),
                    r.college || "N/A",
                    r.refund_amount || r.amount || 0,
                ])
            );

            ws_data.push([]);
            ws_data.push(["Summary"]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Payments", summary.total_payments_today || summary.total_payments_amount || 0]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Refunds", summary.total_refunds_today || summary.total_refunds_amount || 0]);

            const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
            ws['!merges'] = merges;
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Today Summary");

            window.XLSX.writeFile(wb, `Today_Summary_${title}.xlsx`);
        } catch (err) {
            console.error(err);
        }
    };
    const handlePdfDownload = () => {
        if (!summaryData) return;

        const summary = summaryData.summary || {};
        const title = cashier === "All" ? "All Cashiers" : cashier;

        const doc = new jsPDF("landscape");

        doc.setFontSize(16);
        doc.text(`Full Today Summary Report — ${title}`, 14, 15);

        // Summary Table
        autoTable(doc, {
            startY: 30,
            head: [["Net Amount", "Total Payments", "Total Refunds"]],
            body: [[
                formatCurrencyPdf(
                    summary.net_amount_in_cashcounter ??
                    summary.net_amount ??
                    (summary.total_payments_today ?? summary.total_payments_amount ?? 0) -
                    (summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)
                ),
                formatCurrencyPdf(summary.total_payments_today ?? summary.total_payments_amount ?? 0),
                formatCurrencyPdf(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0),
            ]],
            columnStyles: { 0: { halign: "right" }, 1: { halign: "right" }, 2: { halign: "right" } }
        });
        // Payments Table
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology", "Amount"]],
            body: (summaryData.today_payments || []).map((p, index) => [
                index + 1,
                p.roll_no || "N/A",
                p.name || p.first_name || "N/A",
                p.term_name || "N/A",
                p.payment_id || "N/A",
                formatDate(p.payment_date),
                p.college || "N/A",
                Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                p.course_name || "N/A",
                p.technology_name || "N/A",
                p.amount || 0,
            ]),
            columnStyles: { 10: { halign: "right" } }
        });
        // Refunds Table
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Refund Amount"]],
            body: (summaryData.today_refunds || []).map((r, index) => [
                index + 1,
                r.roll_no || "N/A",
                r.name || r.first_name || "N/A",
                r.refund_reason || "N/A",
                r.term_name || r.term || "N/A",
                r.payment_id || "N/A",
                formatDate(r.refund_date || r.refund_given_date),
                r.college || "N/A",
                r.refund_amount || r.amount || 0,
            ]),
            columnStyles: { 8: { halign: "right" } }
        });

        doc.save(`Today_Summary_${title}.pdf`);
    };


    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">

                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title h4">Today’s Summary — {cashier}</h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">

                        <h4 className="fw-bold mb-3">Summary</h4>

                        {summaryData?.summary ? (
                            <div className="row g-3 mb-4">

                                {/* Net Amount */}
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Net Amount</div>
                                        <div className="h4 fw-bold">
                                            {formatCurrency(
                                                summaryData.summary.net_amount_in_cashcounter ??
                                                summaryData.summary.net_amount ??
                                                (summaryData.summary.total_payments_today ?? summaryData.summary.total_payments_amount ?? 0) -
                                                (summaryData.summary.total_refunds_today ?? summaryData.summary.total_refunds_amount ?? 0)
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Total Payments */}
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Total Payments</div>
                                        <div className="h4 fw-bold text-success">
                                            {formatCurrency(summaryData.summary.total_payments_today ?? summaryData.summary.total_payments_amount ?? 0)} (
                                            {summaryData.summary.total_payments_count ?? 0})
                                        </div>
                                    </div>
                                </div>

                                {/* Total Refunds */}
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Total Refunds</div>
                                        <div className="h4 fw-bold text-danger">
                                            {formatCurrency(summaryData.summary.total_refunds_today ?? summaryData.summary.total_refunds_amount ?? 0)} (
                                            {summaryData.summary.total_refunds_count ?? 0})
                                        </div>
                                    </div>
                                </div>

                            </div>
                        ) : (
                            "No summary available."
                        )}

                        {/* Payments Table */}
                        <h4 className="fw-bold mt-4">Payments</h4>
                        <PaymentsTable paymentsData={summaryData?.today_payments || []} />

                        {/* Refunds Table */}
                        <h4 className="fw-bold mt-4">Refunds</h4>
                        <RefundsTable refundData={summaryData?.today_refunds || []} />

                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        <button className="btn btn-success" onClick={handleExcelDownload}>Download Excel</button>
                        <button className='btn btn-danger' onClick={handlePdfDownload}>Download Pdf</button>
                    </div>

                </div>
            </div>
        </div>
    );
}
function PaymentsDateSummaryModal({ show, onClose, summaryData, cashier, date }) {
    const dateLabel = date ? formatDate(date) : 'N/A';

    const handleExcelDownload = () => {
        if (!window.XLSX) return alert("XLSX not loaded!");

        try {
            const summary = summaryData?.summary || {};
            const title = cashier === "All" ? "All Cashiers" : cashier;

            const ws_data = [
                [`Full Summary Report for ${dateLabel} — ${title}`],
                [],
                ["Payments"],
                ["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology", "Amount"]
            ];

            (summaryData?.today_payments || []).forEach((p, index) =>
                ws_data.push([
                    index + 1,
                    p.roll_no || "N/A",
                    p.name || p.first_name || "N/A",
                    p.term_name || "N/A",
                    p.payment_id || "N/A",
                    formatDate(p.payment_date),
                    p.college || "N/A",
                    Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                    p.course_name || "N/A",
                    p.technology_name || "N/A",
                    p.amount || 0,
                ])
            );
            ws_data.push([]);
            ws_data.push(["Refunds"]);
            ws_data.push([
                "S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Refund Amount",
            ]);

            (summaryData?.today_refunds || []).forEach((r, index) =>
                ws_data.push([
                    index + 1,
                    r.roll_no || "N/A",
                    r.name || r.first_name || "N/A",
                    r.refund_reason || "N/A",
                    r.term_name || r.term || "N/A",
                    r.payment_id || "N/A",
                    formatDate(r.refund_date || r.refund_given_date),
                    r.college || "N/A",
                    r.refund_amount || r.amount || 0,
                ])
            );

            ws_data.push([]);
            ws_data.push(["Summary"]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Payments", summary.total_payments_amount || 0]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Refunds", summary.total_refunds_amount || 0]);

            const netAmount =
                summary.net_amount_in_cashcounter ??
                summary.net_amount ??
                (summary.total_payments_amount || 0) - (summary.total_refunds_amount || 0);

            ws_data.push(["", "", "", "", "", "", "", "", "", "Net Amount", netAmount]);

            const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Date Summary");

            window.XLSX.writeFile(wb, `Summary_${dateLabel}_${title}.xlsx`);
        } catch (err) {
            console.error(err);
        }
    };

    const handlePdfDownload = () => {
        if (!summaryData) return;

        const summary = summaryData.summary || {};
        const title = cashier === "All" ? "All Cashiers" : cashier;

        const doc = new jsPDF("landscape");

        doc.setFontSize(16);
        doc.text(`Full Summary Report for ${dateLabel} — ${title}`, 14, 15);

        autoTable(doc, {
            startY: 30,
            head: [["Net Amount", "Total Payments", "Total Refunds"]],
            body: [[
                formatCurrencyPdf(
                    summary.net_amount_in_cashcounter ??
                    summary.net_amount ??
                    (summary.total_payments_amount || 0) - (summary.total_refunds_amount || 0)
                ),
                formatCurrencyPdf(summary.total_payments_amount || 0),
                formatCurrencyPdf(summary.total_refunds_amount || 0),
            ]],
            columnStyles: { 0: { halign: "right" }, 1: { halign: "right" }, 2: { halign: "right" } }
        });

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology", "Amount"]],
            body: (summaryData.today_payments || []).map((p, index) => [
                index + 1,
                p.roll_no || "N/A",
                p.name || p.first_name || "N/A",
                p.term_name || "N/A",
                p.payment_id || "N/A",
                formatDate(p.payment_date),
                p.college || "N/A",
                Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                p.course_name || "N/A",
                p.technology_name || "N/A",
                p.amount || 0,
            ]),
            columnStyles: { 10: { halign: "right" } }
        });

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Refund Amount"]],
            body: (summaryData.today_refunds || []).map((r, index) => [
                index + 1,
                r.roll_no || "N/A",
                r.name || r.first_name || "N/A",
                r.refund_reason || "N/A",
                r.term_name || r.term || "N/A",
                r.payment_id || "N/A",
                formatDate(r.refund_date || r.refund_given_date),
                r.college || "N/A",
                r.refund_amount || r.amount || 0,
            ]),
            columnStyles: { 8: { halign: "right" } }
        });

        doc.save(`Summary_${dateLabel}_${title}.pdf`);
    };

    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title h4">Summary for {dateLabel} — {cashier}</h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        <h4 className="fw-bold mb-3">Summary</h4>

                        {summaryData?.summary ? (
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Net Amount</div>
                                        <div className="h4 fw-bold">
                                            {formatCurrency(
                                                summaryData.summary.net_amount_in_cashcounter ??
                                                summaryData.summary.net_amount ??
                                                (summaryData.summary.total_payments_amount ?? 0) -
                                                (summaryData.summary.total_refunds_amount ?? 0)
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Total Payments</div>
                                        <div className="h4 fw-bold text-success">
                                            {formatCurrency(summaryData.summary.total_payments_amount ?? 0)} (
                                            {summaryData.summary.total_payments_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Total Refunds</div>
                                        <div className="h4 fw-bold text-danger">
                                            {formatCurrency(summaryData.summary.total_refunds_amount ?? 0)} (
                                            {summaryData.summary.total_refunds_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            "No summary available."
                        )}

                        <h4 className="fw-bold mt-4">Payments</h4>
                        <PaymentsTable paymentsData={summaryData?.today_payments || []} />

                        <h4 className="fw-bold mt-4">Refunds</h4>
                        <RefundsTable refundData={summaryData?.today_refunds || []} />
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        <button className="btn btn-success" onClick={handleExcelDownload}>Download Excel</button>
                        <button className="btn btn-danger" onClick={handlePdfDownload}>Download Pdf</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PaymentsTotalSummaryModal({ show, onClose, summaryData, cashier }) {
    // const handleExcelDownload = () => {
    //     if (!window.XLSX) return alert("XLSX not loaded!");

    //     try {
    //         const summary = summaryData?.summary || {};
    //         const title = cashier === "All" ? "All Cashiers" : cashier;

    //         const ws_data = [
    //             [`Full Total Summary Report — ${title}`],
    //             [],
    //             ["Payments"],
    //             // ["S.no", "Roll No.", "Name", "Amount", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology"],
    //             ["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology", "Amount"],
    //         ];

    //         (summaryData?.all_payments || []).forEach((p, index) => {
    //             ws_data.push([
    //                 index + 1,
    //                 p.roll_no || "N/A",
    //                 p.name || p.first_name || "N/A",
    //                 p.term_name || "N/A",
    //                 p.payment_id || "N/A",
    //                 formatDate(p.payment_date),
    //                 p.college || "N/A",
    //                 Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
    //                 p.course_name || "N/A",
    //                 p.technology_name || "N/A",
    //                 p.amount || 0,
    //             ]);
    //         });

    //         ws_data.push([]);
    //         ws_data.push(["Refunds"]);
    //         // ws_data.push(["S.no", "Roll No.", "Name", "Amount", "Reason", "Term", "Payment ID", "Refund Date", "College", "Branch", "Course", "Technology"]);
    //         ws_data.push(["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Branch", "Course", "Technology", "Amount"]);
    //         (summaryData?.refunds_given || []).forEach((r, index) => {
    //             ws_data.push([
    //                 index + 1,
    //                 r.roll_no || "N/A",
    //                 r.name || r.first_name || "N/A",
    //                 r.refund_reason || "N/A",
    //                 r.term_name || "N/A",
    //                 r.payment_id || "N/A",
    //                 formatDate(r.refund_date || r.refund_given_date),
    //                 r.college || "N/A",
    //                 Array.isArray(r.branch) ? r.branch.join(", ") : r.branch || "N/A",
    //                 r.course_name || "N/A",
    //                 r.technology_name || "N/A",
    //                 r.refund_amount || r.amount || 0,
    //             ]);
    //         });
    //         ws_data.push([]);
    //         ws_data.push(["Summary"]);
    //         ws_data.push(["", "", "", "", "", "", "", "", "", "", "Total Payments", summary.total_payments_amount || 0]);
    //         ws_data.push(["", "", "", "", "", "", "", "", "", "", "Total Refunds", summary.total_refunds_amount || 0]);
    //         ws_data.push([
    //             "", "", "", "", "", "", "", "", "", "",
    //             "Net Amount",
    //             (summary.net_amount_in_cashcounter ??
    //                 summary.net_amount ??
    //                 (summary.total_payments_amount || 0) -
    //                 (summary.total_refunds_amount || 0)),
    //         ]);

    //         const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
    //         const wb = window.XLSX.utils.book_new();
    //         window.XLSX.utils.book_append_sheet(wb, ws, "Total Summary");

    //         window.XLSX.writeFile(wb, `Total_Summary_${title}.xlsx`);
    //     } catch (err) {
    //         console.error(err);
    //     }
    // };
    const handleExcelDownload = () => {
        if (!window.XLSX) return alert("XLSX not loaded!");

        try {
            const summary = summaryData?.summary || {};
            const title = cashier === "All" ? "All Cashiers" : cashier;

            const ws_data = [];
            const merges = [];

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Full Total Summary Report — ${title}`]);
            ws_data.push([]);

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push(["Total Thub Payments"]);
            ws_data.push(["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology", "Amount"]);

            (summaryData?.all_payments || []).forEach((p, index) => {
                ws_data.push([
                    index + 1,
                    p.roll_no || "N/A",
                    p.name || p.first_name || "N/A",
                    p.term_name || "N/A",
                    p.payment_id || "N/A",
                    formatDate(p.payment_date),
                    p.college || "N/A",
                    Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                    p.course_name || "N/A",
                    p.technology_name || "N/A",
                    p.amount || 0,
                ]);
            });

            ws_data.push([]);
            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push(["Total Thub Refunds"]);
            ws_data.push(["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Branch", "Course", "Technology", "Amount"]);

            (summaryData?.refunds_given || []).forEach((r, index) => {
                ws_data.push([
                    index + 1,
                    r.roll_no || "N/A",
                    r.name || r.first_name || "N/A",
                    r.refund_reason || "N/A",
                    r.term_name || "N/A",
                    r.payment_id || "N/A",
                    formatDate(r.refund_date || r.refund_given_date),
                    r.college || "N/A",
                    Array.isArray(r.branch) ? r.branch.join(", ") : r.branch || "N/A",
                    r.course_name || "N/A",
                    r.technology_name || "N/A",
                    r.refund_amount || r.amount || 0,
                ]);
            });
            ws_data.push([]);
            ws_data.push(["Summary"]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "", "Total Payments", summary.total_payments_amount || 0]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "", "Total Refunds", summary.total_refunds_amount || 0]);

            const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
            ws['!merges'] = merges;
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Total Summary");

            window.XLSX.writeFile(wb, `Total_Summary_${title}.xlsx`);
        } catch (err) {
            console.error(err);
        }
    };
    const handlePdfDownload = () => {
        if (!summaryData) return;

        const summary = summaryData.summary || {};
        const title = cashier === "All" ? "All Cashiers" : cashier;

        const doc = new jsPDF("landscape");

        doc.setFontSize(16);
        doc.text(`Full Total Summary Report — ${title}`, 14, 15);

        // Summary table
        autoTable(doc, {
            startY: 30,
            head: [["Net Amount", "Total Payments", "Total Refunds"]],
            body: [[
                formatCurrencyPdf(
                    summary.net_amount_in_cashcounter ??
                    summary.net_amount ??
                    (summary.total_payments_amount || 0) -
                    (summary.total_refunds_amount || 0)
                ),
                formatCurrencyPdf(summary.total_payments_amount || 0),
                formatCurrencyPdf(summary.total_refunds_amount || 0),
            ]],
            columnStyles: { 0: { halign: "right" }, 1: { halign: "right" }, 2: { halign: "right" } }
        });
        // Payments table
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Course", "Technology", "Amount"]],
            body: (summaryData.all_payments || []).map((p, index) => [
                index + 1,
                p.roll_no || "N/A",
                p.name || p.first_name || "N/A",
                p.term_name || "N/A",
                p.payment_id || "N/A",
                formatDate(p.payment_date),
                p.college || "N/A",
                Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                p.course_name || "N/A",
                p.technology_name || "N/A",
                p.amount || 0,
            ]),
            columnStyles: { 10: { halign: "right" } }
        });

        // Refunds table
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Branch", "Course", "Technology", "Amount"]],
            body: (summaryData.refunds_given || []).map((r, index) => [
                index + 1,
                r.roll_no || "N/A",
                r.name || r.first_name || "N/A",
                r.refund_reason || "N/A",
                r.term_name || "N/A",
                r.payment_id || "N/A",
                formatDate(r.refund_date || r.refund_given_date),
                r.college || "N/A",
                Array.isArray(r.branch) ? r.branch.join(", ") : r.branch || "N/A",
                r.course_name || "N/A",
                r.technology_name || "N/A",
                r.refund_amount || r.amount || 0,
            ]),
            columnStyles: { 11: { halign: "right" } }
        });
        doc.save(`Total_Summary_${title}.pdf`);
    };



    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">

                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title h4">Full Total Summary — {cashier}</h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        <h4 className="fw-bold mb-3">Summary</h4>

                        {summaryData?.summary ? (
                            <div className="row g-3 mb-4">

                                {/* Net */}
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Net Amount</div>
                                        <div className="h4 fw-bold">
                                            {formatCurrency(
                                                summaryData.summary.net_amount_in_cashcounter ??
                                                summaryData.summary.net_amount ??
                                                (summaryData.summary.total_payments_amount ?? 0) -
                                                (summaryData.summary.total_refunds_amount ?? 0)
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Payments */}
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Total Payments</div>
                                        <div className="h4 fw-bold text-success">
                                            {formatCurrency(summaryData.summary.total_payments_amount ?? 0)} (
                                            {summaryData.summary.total_payments_count ?? 0})
                                        </div>
                                    </div>
                                </div>

                                {/* Refunds */}
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3 shadow-sm">
                                        <div className="small text-muted">Total Refunds</div>
                                        <div className="h4 fw-bold text-danger">
                                            {formatCurrency(summaryData.summary.total_refunds_amount ?? 0)} (
                                            {summaryData.summary.total_refunds_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            "No summary available."
                        )}

                        {/* Payments Table */}
                        <h4 className="fw-bold mt-4">Payments</h4>
                        <PaymentsTable paymentsData={summaryData?.all_payments || []} />

                        {/* Refunds Table */}
                        <h4 className="fw-bold mt-4">Refunds</h4>
                        <RefundsTable refundData={summaryData?.refunds_given || []} />
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        <button className="btn btn-success" onClick={handleExcelDownload}>Download Excel</button>
                        <button className="btn btn-danger" onClick={handlePdfDownload}>Download PDF</button>

                    </div>

                </div>
            </div>
        </div>
    );
}
